import _ from 'lodash';
import { observable } from 'mobx';
import type { HotelElementResponseArgs } from './hotel_element2';
import HotelElement, { convertHotelElementResponseToArgs } from './hotel_element2';
import type { NonOrderItemPriceResponseArgs } from './non_order_item_price2';
import NonOrderItemPrice, { convertNonOrderItemPriceResponseToArgs } from './non_order_item_price2';
import type { NonOrderItemMappingResponseArgs } from './non_order_item_mapping2';
import NonOrderItemMapping, { convertNonOrderItemMappingResponseToArgs } from './non_order_item_mapping2';
import type { TransportElementResponseArgs } from './transport_element2';
import TransportElement, { convertTransportElementResponseToArgs } from './transport_element2';

export type NonOrderItemResponseArgs = {
  id: number;
  index?: number;
  price: NonOrderItemPriceResponseArgs;
  people_num: number;
  non_order_item_mappings?: NonOrderItemMappingResponseArgs[];
  transports?: TransportElementResponseArgs[];
  hotels?: HotelElementResponseArgs[];
  payment_type?: string;
  request_type?: string;
  is_need?: boolean;
  supplier?: string;
  trip_type?: string;
  trip_type_text?: string;
  element_type?: string;
};

export type NonOrderItemArgs = {
  id: number;
  index?: number;
  price: NonOrderItemPrice;
  peopleNum: number;
  nonOrderItemMappings: NonOrderItemMapping[];
  transports: TransportElement[];
  hotels: HotelElement[];
  paymentType?: string;
  requestType?: string;
  isNeed?: boolean;
  supplier?: string;
  tripType?: string;
  tripTypeText?: string;
  elementType?: string;
};

export const convertNonOrderItemResponseToArgs = (response: NonOrderItemResponseArgs) => {
  const args: NonOrderItemArgs = {
    id: response.id,
    index: response.index,
    price: new NonOrderItemPrice(convertNonOrderItemPriceResponseToArgs(response.price)),
    peopleNum: response.people_num,
    nonOrderItemMappings: response.non_order_item_mappings
      ? response.non_order_item_mappings.map(
          mapping => new NonOrderItemMapping(convertNonOrderItemMappingResponseToArgs(mapping))
        )
      : [],
    transports:
      response.transports?.map(r => new TransportElement(convertTransportElementResponseToArgs(r))) || [],
    hotels: response.hotels?.map(r => new HotelElement(convertHotelElementResponseToArgs(r))) || [],
    paymentType: response.payment_type,
    requestType: response.request_type,
    isNeed: response.is_need,
    supplier: response.supplier,
    tripType: response.trip_type,
    tripTypeText: response.trip_type_text,
    elementType: response.element_type
  };
  return args;
};

export default class NonOrderItem {
  @observable id: number;
  @observable index?: number;
  @observable price: NonOrderItemPrice;
  @observable peopleNum: number;
  @observable nonOrderItemMappings: NonOrderItemMapping[];
  @observable transports: TransportElement[];
  @observable hotels: HotelElement[];
  @observable elements: (TransportElement | HotelElement)[];
  @observable paymentType: string;
  @observable requestType?: string;
  @observable isNeed: boolean;
  @observable supplier?: string;
  @observable tripType: string;
  @observable tripTypeText?: string;
  @observable elementType?: string;

  constructor(args: NonOrderItemArgs) {
    this.id = args.id;
    this.index = args.index;
    this.price = args.price;
    this.peopleNum = args.peopleNum;
    this.nonOrderItemMappings = args.nonOrderItemMappings;
    this.transports = args.transports;
    this.hotels = args.hotels;
    this.elements = [...this.transports, ...this.hotels];
    this.paymentType = args.paymentType || 'cash';
    this.requestType = args.requestType || 'simple_request';
    this.isNeed = args.isNeed ?? true;
    this.supplier = args.supplier;
    this.tripType = args.tripType || 'round_trip';
    this.tripTypeText = args.tripTypeText;
    this.elementType = args.elementType;
  }

  get startDate() {
    const dates = _.compact(this.elements.map(element => element.startTime));
    return _.sortBy(dates, date => date.valueOf())[0];
  }

  get startDateTime() {
    return this.startDate;
  }

  get endDate() {
    const dates = _.compact(this.elements.map(element => element.endTime));
    return _.sortBy(dates, date => date.valueOf()).slice(-1)[0];
  }

  get totalPrice() {
    if (this.hotels.length > 0) {
      return this.price.totalPrice * this.hotels[0].roomNum;
    }
    return this.price.totalPrice * this.peopleNum;
  }

  get isSimpleRequest() {
    return this.requestType === 'simple_request';
  }

  get typeStr() {
    return this.hotels.length > 0 ? 'ホテル' : '経路';
  }

  get paymentTypeStr() {
    switch (this.paymentType) {
      case 'cash':
        return '現金';
      case 'ic':
        return 'IC';
      case 'credit':
        return 'クレジット';
      case 'on_the_company':
        return '会社請求';
      case 'on_me':
        return '個人立替';
      default:
        return 'その他';
    }
  }

  isElementTypes(types: string[]) {
    if (types[0] === 'hotel') {
      return this.hotels.length > 0;
    }
    return this.transports.length > 0 && types.indexOf(this.transports[0].transportType) > -1;
  }

  get updateParams() {
    if (_.isEmpty(this.elements) || (!this.id && !this.isNeed)) {
      return null;
    }

    return {
      id: this.id,
      price: this.price.updateParams,
      elements: this.elements.map(element => element.updateParams),
      people_num: this.peopleNum,
      payment_type: this.paymentType,
      request_type: this.requestType,
      is_need: this.isNeed,
      supplier: this.supplier,
      trip_type: this.tripType
    };
  }
}
